import type { ApiClient } from '../client/api-client';
import type {
  EmailOtpDto,
  IpWhitelistState,
  LoginHistoryPage,
  SecondFactor,
  SecuritySettings,
  SecuritySettingsPatch,
  SessionItem,
  TrustedDeviceItem,
  TwoFaChangeResult,
  TwoFaSetupDto,
  TwoFaSetupMethod,
  WithdrawalAddressState,
} from '../contracts/security';

const resourceId = (id: string | number): string => encodeURIComponent(String(id));

export function createSecurityService(client: ApiClient) {
  return {
    getSettings: () => client.get<SecuritySettings>('/app/v1/security/settings'),
    patchSettings: (
      payload: Partial<Pick<SecuritySettingsPatch, 'sessionIpBinding' | 'withdrawal2faRequired'>> & {
        secondFactor?: SecondFactor;
      },
    ) => client.patch<SecuritySettingsPatch>('/app/v1/security/settings', payload),
    sendEmailOtp: (purpose: string) =>
      client.post<EmailOtpDto>('/app/v1/security/otp/email', { purpose }),
    setupTwoFa: (method: TwoFaSetupMethod) =>
      client.post<TwoFaSetupDto>('/app/v1/security/2fa/setup', { method }),
    changeTwoFa: (payload: { method: TwoFaSetupMethod; code: string; secondFactor: SecondFactor }) =>
      client.post<TwoFaChangeResult>('/app/v1/security/2fa/change', payload),
    regenerateBackupCodes: (secondFactor: SecondFactor) =>
      client.post<{ codes: string[] }>('/app/v1/security/2fa/backup-codes', { secondFactor }),
    getSessions: () => client.get<SessionItem[]>('/app/v1/security/sessions'),
    revokeSession: (id: string) => client.delete<void>(`/app/v1/security/sessions/${resourceId(id)}`),
    terminateOtherSessions: (secondFactor: SecondFactor) =>
      client.post<{ terminated: number }>('/app/v1/security/sessions/terminate-others', { secondFactor }),
    getLoginHistory: (params: { limit?: number; before?: string } = {}) => {
      const search = new URLSearchParams();
      search.set('limit', String(params.limit ?? 20));
      if (params.before) search.set('before', params.before);
      return client.get<LoginHistoryPage>(`/app/v1/security/login-history?${search.toString()}`);
    },
    getTrustedDevices: () => client.get<TrustedDeviceItem[]>('/app/v1/security/trusted-devices'),
    revokeTrustedDevice: (id: number) =>
      client.delete<void>(`/app/v1/security/trusted-devices/${resourceId(id)}`),
    getIpWhitelist: () => client.get<IpWhitelistState>('/app/v1/security/ip-whitelist'),
    addIpWhitelist: (payload: { cidr: string; comment?: string; secondFactor: SecondFactor }) =>
      client.post<IpWhitelistState['items'][number]>('/app/v1/security/ip-whitelist', payload),
    deleteIpWhitelist: (id: number, secondFactor: SecondFactor) =>
      client.delete<void>(`/app/v1/security/ip-whitelist/${resourceId(id)}`, { secondFactor }),
    setIpWhitelistEnabled: (enabled: boolean, secondFactor: SecondFactor) =>
      client.patch<IpWhitelistState>('/app/v1/security/ip-whitelist', { enabled, secondFactor }),
    getWithdrawalAddresses: () => client.get<WithdrawalAddressState>('/app/v1/security/withdrawal-addresses'),
    addWithdrawalAddress: (payload: {
      network: string;
      address: string;
      label?: string;
      secondFactor: SecondFactor;
    }) => client.post<WithdrawalAddressState['items'][number]>('/app/v1/security/withdrawal-addresses', payload),
    deleteWithdrawalAddress: (id: number, secondFactor: SecondFactor) =>
      client.delete<void>(`/app/v1/security/withdrawal-addresses/${resourceId(id)}`, { secondFactor }),
    setWithdrawalAddressesEnabled: (enabled: boolean, secondFactor: SecondFactor) =>
      client.patch<WithdrawalAddressState>('/app/v1/security/withdrawal-addresses', { enabled, secondFactor }),
  };
}

export type SecurityService = ReturnType<typeof createSecurityService>;
